import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { jwtDecode } from 'jwt-decode';
import { MessageNotificationService } from 'message-notification-service-lib';
import { mergeMap, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import { environment } from '../../../environments/environment';
import { CalendarDataService } from '../../services/calendar-data.service';

enum CalendarStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  ERROR = 'error',
}

enum CalendarType {
  OUTLOOK = 'outlook',
}

@Component({
  selector: 'app-calendar-settings-dialog',
  templateUrl: './calendar-settings-dialog.component.html',
  styleUrls: ['./calendar-settings-dialog.component.scss'],
  standalone: false,
})
export class CalendarSettingsDialogComponent implements OnInit, OnDestroy {
  outlookLoading = false;
  outlookEmail = '';
  outlookStatus: CalendarStatus = CalendarStatus.CONNECTING;
  outlookCalendarId = '';

  private pollingInterval: any;

  constructor(
    private dialogRef: MatDialogRef<CalendarSettingsDialogComponent>,
    private identityStorage: IdentityStorage,
    private calendarDataService: CalendarDataService,
    private messageNotificationService: MessageNotificationService
  ) {}

  ngOnInit(): void {
    this.checkUserCalendars();
  }

  private async checkUserCalendars(): Promise<void> {
    const identity = this.identityStorage.get();
    if (!identity?.token) {
      return;
    }

    try {
      const decoded: any = jwtDecode(identity.token);
      const corpId = decoded.corporationId;
      const userId = decoded.userId || decoded.user_id;

      const response = (await this.calendarDataService
        .getUserCalendars(corpId, userId)
        .toPromise()) as any;

      if (response.success && response.calendars) {
        const outlookCalendar = response.calendars.find(
          (cal) => cal.platform === 'microsoft_outlook'
        );

        if (outlookCalendar) {
          this.outlookStatus = outlookCalendar.status;
          this.outlookEmail = outlookCalendar.email || '';
          this.outlookCalendarId = outlookCalendar.calendarId;

          if (outlookCalendar.status === CalendarStatus.CONNECTING) {
            this.outlookLoading = true;
            this.startPolling(outlookCalendar.calendarId);
          } else if (outlookCalendar.status === CalendarStatus.CONNECTED) {
            this.outlookStatus = CalendarStatus.CONNECTED;
            this.outlookLoading = false;
          }
        } else {
          this.outlookStatus = CalendarStatus.DISCONNECTED;
          this.outlookLoading = false;
        }
      } else {
        this.outlookStatus = CalendarStatus.DISCONNECTED;
        this.outlookLoading = false;
      }
    } catch (error) {
      console.error('Failed to check user calendars:', error);
    }
  }

  selectCalendar(type: string): void {
    if (type === CalendarType.OUTLOOK) {
      this.outlookLoading = true;
      this.outlookStatus = CalendarStatus.CONNECTING;

      // todo just send what we need
      const identity = this.identityStorage.get();
      if (identity?.token) {
        const decoded: any = jwtDecode(identity.token);
        const popup = window.open(
          `${environment.calendarGatewayUrl}/signin/${environment.calendarClientId}`,
          '_blank',
          'width=800,height=600'
        );
        this.waitForOAuthCompletion(popup, decoded)
          .pipe(
            mergeMap((code) => this.calendarDataService.createAccessToken(code)),
            map((code) => this.setupCalendarByEmail(code.tokenId))
          )
          .subscribe();
      }
    }

    console.log(`Selected ${type} calendar`);
  }

  private waitForOAuthCompletion(popup: Window | null, jwtData: any): Observable<string> {
    return new Observable((subscriber) => {
      const intervalId = setInterval(() => {
        if (popup.closed) {
          console.log('Calendar redirect handler window closed.');
          window.removeEventListener('message', listener);
          window.clearInterval(intervalId);
          subscriber.error('The user did not login.');
        }
      }, 500);

      const listener = (e: MessageEvent) => {
        if (e.data?.responseUrl) {
          window.clearInterval(intervalId);
          window.removeEventListener('message', listener);
          popup.close();
          const url = new URL(e.data.responseUrl);
          subscriber.next(url.searchParams.get('code') as string);
          subscriber.complete();
        } else if (e.data?.error) {
          window.clearInterval(intervalId);
          window.removeEventListener('message', listener);
          popup.close();
          subscriber.error(`${e.data.error}: ${e.data.errorDescription || 'No description provided.'}`);
        }
      };

      window.addEventListener('message', listener);
    });
  }

  private async setupCalendarByEmail(emailCalendarId: string): Promise<void> {
    const identity = this.identityStorage.get();
    if (!identity?.token) {
      this.outlookLoading = false;
      this.outlookStatus = CalendarStatus.DISCONNECTED;
      return;
    }

    try {
      const decoded: any = jwtDecode(identity.token);
      const corpId = decoded.corporationId.toString();
      const email = decoded.email;
      const userId = decoded.userId.toString();

      const response = (await this.calendarDataService
        .setupCalendar(corpId, email, userId, 'microsoft_outlook', emailCalendarId)
        .toPromise()) as any;

      if (response.success && response.status === CalendarStatus.CONNECTING) {
        this.startPolling(response.calendarId);
      } else if (response.status === CalendarStatus.CONNECTED) {
        this.onCalendarConnected(response);
      }
    } catch (error) {
      console.error('Calendar setup failed:', error);
      this.outlookLoading = false;
      this.outlookStatus = CalendarStatus.ERROR;
    }
  }

  private startPolling(calendarId: string): void {
    this.pollingInterval = setInterval(async () => {
      try {
        const identity = this.identityStorage.get();
        if (!identity?.token) {
          return;
        }

        const decoded: any = jwtDecode(identity.token);
        const corpId = decoded.corporationId;
        const userId = decoded.userId || decoded.user_id;

        const response = (await this.calendarDataService
          .getUserCalendars(corpId, userId)
          .toPromise()) as any;

        if (response.success && response.calendars) {
          const outlookCalendar = response.calendars.find((cal) => cal.calendarId === calendarId);

          if (outlookCalendar) {
            const status = outlookCalendar.status;

            if (
              status === CalendarStatus.CONNECTED ||
              status === 'failed' ||
              status === 'error' ||
              status === CalendarStatus.DISCONNECTED
            ) {
              this.stopPolling();

              if (status === CalendarStatus.CONNECTED) {
                this.onCalendarConnected({ status, calendarId });
              } else {
                this.onCalendarFailed(status);
              }
            }
          }
        }
      } catch (error) {
        console.error('Polling failed:', error);
        this.stopPolling();
        this.outlookLoading = false;
        this.outlookStatus = CalendarStatus.ERROR;
      }
    }, 5000);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private onCalendarConnected(response: any): void {
    console.log('Calendar connected successfully!', response);
    this.messageNotificationService.success(`Calendar connected successfully!`);
    this.outlookLoading = false;
    this.outlookStatus = CalendarStatus.CONNECTED;
    // Refresh calendar data to get the email
    this.checkUserCalendars();
  }

  private onCalendarFailed(status: string): void {
    console.log('Calendar connection failed:', status);
    this.messageNotificationService.success(`Failed to connect to calendar. Please try again.`);
    this.outlookLoading = false;
    this.outlookStatus = CalendarStatus.ERROR;
  }

  async signOut(type: string): Promise<void> {
    this.stopPolling();
    this.outlookLoading = true;
    this.outlookEmail = 'Signing out...';

    const identity = this.identityStorage.get();
    if (!identity?.token) {
      return;
    }

    try {
      const decoded: any = jwtDecode(identity.token);
      const corpId = decoded.corporationId;
      const userId = decoded.userId || decoded.user_id;

      const response = (await this.calendarDataService
        .getUserCalendars(corpId, userId)
        .toPromise()) as any;

      if (response.success && response.calendars) {
        const outlookCalendar = response.calendars.find(
          (cal) => cal.calendarId === this.outlookCalendarId
        );

        await this.calendarDataService
          .deleteUserCalendar(
            this.outlookCalendarId,
            outlookCalendar.emailCalendarId,
            environment.calendarClientId
          )
          .toPromise();

        if (type === CalendarType.OUTLOOK) {
          this.outlookLoading = false;
          this.outlookStatus = CalendarStatus.DISCONNECTED;
          this.outlookEmail = '';
          this.outlookCalendarId = '';
        }

        console.log(`Signed out of ${type} calendar`);
        this.messageNotificationService.success(`Successfully signed out of ${type} calendar.`);
      }
    } catch (error) {
      console.error('Failed to sign out:', error);
      this.messageNotificationService.error('Failed to sign out of calendar. Please try again');
    }
  }

  close(): void {
    this.stopPolling();
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
